import { useCallback, useRef, useState, type RefObject } from 'react';
import { newStrokeId, type Point, type Stroke } from '../lib/strokes';

export type Tool = 'pen' | 'eraser';

export type DrawingState = {
  color: string;
  size: number;
  tool: Tool;
};

type Args = {
  state: DrawingState;
  onStrokeComplete: (s: Stroke) => void;
  onErase: (pointN: Point) => void;
  containerRef: RefObject<HTMLElement | null>;
};

export function useDrawing({ state, onStrokeComplete, onErase, containerRef }: Args) {
  const [currentStroke, setCurrentStroke] = useState<Stroke | null>(null);
  const activePointerId = useRef<number | null>(null);

  const toNormalized = useCallback(
    (e: React.PointerEvent): Point => {
      const el = containerRef.current;
      if (!el) return { x: 0, y: 0, pressure: 0.5 };
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      return {
        x: clamp01(x),
        y: clamp01(y),
        pressure: e.pressure > 0 ? e.pressure : 0.5,
      };
    },
    [containerRef]
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (activePointerId.current !== null) return;
      activePointerId.current = e.pointerId;
      try {
        (e.target as Element).setPointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
      const pt = toNormalized(e);
      if (state.tool === 'eraser') {
        onErase(pt);
        return;
      }
      setCurrentStroke({
        id: newStrokeId(),
        color: state.color,
        size: state.size,
        points: [pt],
      });
    },
    [state, toNormalized, onErase]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (activePointerId.current !== e.pointerId) return;
      const pt = toNormalized(e);
      if (state.tool === 'eraser') {
        onErase(pt);
        return;
      }
      setCurrentStroke((prev) => (prev ? { ...prev, points: [...prev.points, pt] } : prev));
    },
    [state, toNormalized, onErase]
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (activePointerId.current !== e.pointerId) return;
      activePointerId.current = null;
      setCurrentStroke((prev) => {
        if (prev && state.tool === 'pen' && prev.points.length > 0) {
          onStrokeComplete(prev);
        }
        return null;
      });
    },
    [state.tool, onStrokeComplete]
  );

  return {
    currentStroke,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onPointerLeave: onPointerUp,
    },
  };
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
